import { useFormikContext } from 'formik';

import { FormSection } from '@@/form-components/FormSection';
import { FormControl } from '@@/form-components/FormControl';
import { FormError } from '@@/form-components/FormError';
import { SwitchField } from '@@/form-components/SwitchField';
import { Input } from '@@/form-components/Input';
import { TextArea } from '@@/form-components/Input/Textarea';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { ColorPicker } from '@@/form-components/ColorPicker/ColorPicker';

import {
  ChangeConfirmationFormValues,
  ConfirmSeverity,
  getFormLevelError,
} from './types';

const SEVERITY_OPTIONS: { value: ConfirmSeverity; label: string }[] = [
  { value: 'default', label: 'Default' },
  { value: 'warning', label: 'Warning' },
  { value: 'error', label: 'Error' },
];

export function ChangeConfirmationFormFields() {
  const { values, setFieldValue, errors } =
    useFormikContext<ChangeConfirmationFormValues>();
  const formLevelError = getFormLevelError(errors);

  return (
    <>
      <FormSection title="Highlight environment">
        <p className="text-muted text-xs">
          Visually highlight the environment with a colored border in the UI.
        </p>
        <SwitchField
          label="Highlight environment"
          checked={values.highlightEnabled}
          onChange={(v) => setFieldValue('highlightEnabled', v)}
          labelClass="col-sm-3 col-lg-2"
          fieldClass="!mb-4"
          data-cy="highlight-enabled-switch"
        />
        {values.highlightEnabled && (
          <>
            <FormControl
              label="Highlight color"
              inputId="highlightColor"
              errors={errors.highlightColor}
              required
            >
              <ColorPicker
                value={values.highlightColor ?? ''}
                onChange={(v) => setFieldValue('highlightColor', v)}
                data-cy="highlight-color-picker"
              />
            </FormControl>
            <FormControl
              label="Banner message"
              inputId="bannerMessage"
              tooltip="Leave empty to show a colored border only"
              errors={errors.bannerMessage}
            >
              <Input
                id="bannerMessage"
                value={values.bannerMessage ?? ''}
                onChange={(e) => setFieldValue('bannerMessage', e.target.value)}
                placeholder="e.g. '⚠️ Production environment contact on-call.corp.com for details ⚠️'"
                data-cy="banner-message-input"
              />
            </FormControl>
          </>
        )}
      </FormSection>

      <FormSection title="Change confirmation">
        <p className="text-muted text-xs">
          Prompt users before making any changes in the attached environments.
          Existing confirmation prompts will be unchanged.
        </p>
        <SwitchField
          label="Enable change confirmation"
          checked={values.confirmEnabled}
          onChange={(v) => setFieldValue('confirmEnabled', v)}
          labelClass="col-sm-3 col-lg-2"
          fieldClass="!mb-4"
          data-cy="confirm-enabled-switch"
        />
        {values.confirmEnabled && (
          <>
            <FormControl
              label="Confirmation title"
              inputId="confirmTitle"
              errors={errors.confirmTitle}
              required
            >
              <Input
                id="confirmTitle"
                value={values.confirmTitle ?? ''}
                onChange={(e) => setFieldValue('confirmTitle', e.target.value)}
                data-cy="confirm-title-input"
                placeholder="e.g. 'Are you sure?'"
              />
            </FormControl>
            <FormControl
              label="Confirmation message"
              inputId="confirmMessage"
              errors={errors.confirmMessage}
              required
            >
              <TextArea
                id="confirmMessage"
                className="form-control resize-y"
                value={values.confirmMessage ?? ''}
                onChange={(e) =>
                  setFieldValue('confirmMessage', e.target.value)
                }
                rows={3}
                data-cy="confirm-message-textarea"
                placeholder="e.g. 'This action will make changes to this environment. Do you want to continue?'"
              />
            </FormControl>
            <FormControl label="Confirmation style" inputId="confirmSeverity">
              <PortainerSelect<ConfirmSeverity>
                inputId="confirmSeverity"
                options={SEVERITY_OPTIONS}
                value={values.confirmSeverity}
                onChange={(value) =>
                  setFieldValue('confirmSeverity', value ?? 'default')
                }
                isClearable={false}
                bindToBody
                data-cy="confirm-severity-select"
              />
            </FormControl>
          </>
        )}
        {formLevelError && <FormError>{formLevelError}</FormError>}
      </FormSection>
    </>
  );
}
